import { Component, Input, OnChanges, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { NgSelectComponent } from '@ng-select/ng-select';
import { combineLatest, Subscription } from 'rxjs';
import { distinctUntilChanged, first, pairwise, startWith } from 'rxjs/operators';
import { EmployeeService } from 'src/app/services/employee.service';
import { LeaveService } from 'src/app/services/leave.service';
import { Leave } from '../../../../../app/classes/leave';
import { environment } from '../../../../../environments/environment';
import { LeaveUserDate } from '../../../../classes/leave-user-date';
import { PlanningHasEntity } from '../../../../classes/planning-has-entity.class';
import { UserPlanning } from '../../../../classes/user-planning';
import { User } from '../../../../classes/user.class';
import { AddressDialogComponent } from '../../../../pages/transport-planning/planning-detail-employees/address-dialog/address-dialog.component';
import { ConfirmDialogService } from '../../../../services/confirm-dialog-service/confirm-dialog.service';
import { EntityField, EntityId, EntityType, UserId } from '../../../../services/entities/entity-type.class';
import {
    EmployeePlanningFg,
    EntityTypeService,
    PheFormData,
    PlanningFormData,
} from '../../../../services/entity-type.service';
import { LocationService } from '../../../../services/location.service';
import { PlanningHasService } from '../../../../services/planning/planning-has.service';
import { PlanningService } from '../../../../services/planning/planning.service';
import { UserPlanningService } from '../../../../services/planning/user-planning.service';
import { UserService } from '../../../../services/user/user.service';
import { Utils } from '../../../../utils.class';
import { localEmployeeAvailableValidator } from '../../../../validators/local-employee-available.validator';

interface HiringSelectItem {
    id: null;
    name: string;
    companyName?: string;
    hiringId?: number;
    isHiring: true;
    function?: string;
}

type EmployeeSelectItem =
    | (User & {
          leaves?: LeaveUserDate[];
          companyName?: string;
          isHiring?: never;
      })
    | HiringSelectItem;

@Component({
    selector: 'app-trackline-employee-planning',
    standalone: false,
    templateUrl: './trackline-employee-planning.component.html',
    styleUrl: './trackline-employee-planning.component.scss',
})
export class TracklineEmployeePlanningComponent implements OnInit, OnDestroy, OnChanges {
    saving: boolean;
    employees: User[];
    employeeMap: Map<number, User>;
    filteredEmployees: EmployeeSelectItem[];
    sums: { function: string; amount: number }[] = [];
    fcEmployee = new FormControl();
    hiringStaffPheFields: PheFormData[] = [];
    hiringStaffTableHeader: EntityField[] = [];
    private hiringStaffEntityType: EntityType;

    addHiringTagFn = (term: string) => ({ id: null, name: term, isHiring: true as const });

    searchFn = (term: string, item: EmployeeSelectItem) => {
        const t = term.toLowerCase();
        return !!(
            item.name?.toLowerCase().includes(t) ||
            (item as User).function?.toLowerCase().includes(t) ||
            item.companyName?.toLowerCase().includes(t)
        );
    };

    @Input() formData: PlanningFormData;
    leaves: Leave[];
    employeeLeaves: Record<number, LeaveUserDate[]> = {};

    @Input() fixedDate: Date;
    filterFunction: string | null = null;
    @ViewChild('addEmployeeSelect') addEmployeeSelect: NgSelectComponent;

    PlanningStatus = environment.status.Planning;

    mainPlanning: PlanningHasEntity;
    private subscriptions = new Subscription();

    constructor(
        private dialog: MatDialog,
        private confirmDialog: ConfirmDialogService,
        private userPlanningService: UserPlanningService,
        private locationService: LocationService,
        private userService: UserService,
        private entityTypeService: EntityTypeService,
        private planningHasService: PlanningHasService,
        private leaveService: LeaveService,
        private employeeService: EmployeeService,
        private planningService: PlanningService
    ) {}

    ngOnInit(): void {
        this.hiringStaffEntityType = environment.entitytypes['hiringStaff'] as EntityType;
        if (this.hiringStaffEntityType) {
            this.hiringStaffTableHeader = this.entityTypeService.getTableHeaders(this.hiringStaffEntityType);
            this.refreshHiringStaffPheFields();
            this.subscriptions.add(
                this.formData.form.controls.planningHas.valueChanges.subscribe(() => {
                    this.refreshHiringStaffPheFields();
                })
            );
        }
        this.subscriptions.add(
            this.formData.form.controls.employeePlanning.valueChanges.subscribe(value => {
                value.forEach(v => {
                    this.setEmployeeLeaves(v.user_id);
                });
            })
        );

        this.subscriptions.add(
            this.userService.getMap(true).subscribe(userMap => {
                this.employeeMap = userMap;
            })
        );

        const planningForm = this.formData.form.controls.planning;
        const pheMainForm = this.formData.form.controls.mainPlanningHas.controls.phe;

        ['asphalt_performer', 'performer'].forEach(field => {
            if (planningForm.get(field)) {
                this.subscriptions.add(
                    planningForm.get(field).valueChanges.subscribe(newPerformerId => {
                        const previousPerformerId = planningForm.value[field] as number;

                        if (previousPerformerId !== newPerformerId) {
                            this.employeeService
                                .getDefaultEmployees(
                                    this.employees,
                                    this.mainPlanning?.entity?.default_employees ?? [],
                                    [previousPerformerId]
                                )
                                .forEach(user => {
                                    const fgEmployee = this.formData.form.controls.employeePlanning.controls.find(
                                        c => c.value.user_id === user.id
                                    );

                                    if (fgEmployee) {
                                        this.deleteEmployeeFg(fgEmployee);
                                    }
                                });

                            this.addDefaults();
                        }
                    })
                );
            }
        });

        if (planningForm.get('location')) {
            this.subscriptions.add(
                planningForm
                    .get('location')
                    .valueChanges.pipe(distinctUntilChanged())
                    .subscribe(newLocation => {
                        const previousLocation = this.formData.form.controls.planning.value['location'];

                        this.formData.form.controls.employeePlanning.controls.forEach(fg => {
                            if (fg.controls.work_begin.value === previousLocation) {
                                fg.controls.work_begin.setValue(newLocation);
                            }
                            if (fg.controls.work_end.value === previousLocation) {
                                fg.controls.work_end.setValue(newLocation);
                            }
                        });
                    })
            );
        }

        if (planningForm.get('status_key')) {
            this.subscriptions.add(
                planningForm.get('status_key').valueChanges.subscribe(newStatusId => {
                    if (
                        this.formData.form.controls.planning.value['status_key'] !== newStatusId &&
                        newStatusId === this.PlanningStatus.PlanningDefinitief
                    ) {
                        this.addDefaults();
                    }
                })
            );
        }

        this.subscriptions.add(
            pheMainForm
                .get('begindate')
                .valueChanges.pipe(startWith(pheMainForm.get('begindate').value), pairwise())
                .subscribe(([previousStartTime, newStartTime]) => {
                    const previousTime = Utils.getTimeOrNull(previousStartTime);

                    this.formData.form.controls.employeePlanning.controls.forEach(fg => {
                        if (
                            previousTime === Utils.getTimeOrNull(fg.value.begindate) &&
                            Utils.getTimeOrNull(fg.value.begindate) !== Utils.getTimeOrNull(newStartTime)
                        ) {
                            fg.controls.begindate.setValue(new Date(newStartTime));
                        }
                    });
                })
        );

        this.subscriptions.add(
            pheMainForm
                .get('enddate')
                .valueChanges.pipe(startWith(pheMainForm.get('enddate').value), pairwise())
                .subscribe(([previousEndTime, newEndTime]) => {
                    const previousTime = Utils.getTimeOrNull(previousEndTime);

                    this.formData.form.controls.employeePlanning.controls.forEach(fg => {
                        if (
                            previousTime === Utils.getTimeOrNull(fg.value.enddate) &&
                            Utils.getTimeOrNull(fg.value.enddate) !== Utils.getTimeOrNull(newEndTime)
                        ) {
                            fg.controls.enddate.setValue(new Date(newEndTime));
                        }

                        this.setEmployeeLeaves(fg.value.user_id);
                    });
                })
        );

        this.subscriptions.add(
            this.userService
                .getList(false, false)
                .pipe(first())
                .subscribe(users => {
                    this.employees = users;
                    const nonExistingUsers = this.formData.orginal?.user_planning
                        .map(up => up.user)
                        .filter(u => !this.employees.find(e => e.id === u.id));
                    if (nonExistingUsers?.length) {
                        this.employees.push(...nonExistingUsers);
                    }

                    this.formData.orginal?.user_planning?.forEach(userPlanning => {
                        const fg = this.createEmployeeFg(
                            +userPlanning.user_id,
                            {
                                id: userPlanning.id,
                                _moved_from_id: userPlanning?._movedFrom?.id,
                                planning_id: userPlanning.planning_id,
                                begindate: userPlanning.begindate,
                                enddate: userPlanning.enddate,
                                comment: userPlanning.comment,
                                updated_at: userPlanning.updated_at,
                                origin: userPlanning.origin,
                                destination: userPlanning.destination,
                                work_begin: userPlanning.work_begin,
                                work_end: userPlanning.work_end,
                            },
                            userPlanning._movedFrom
                        );
                        this.formData.form.controls.employeePlanning.push(fg);
                        fg.markAsPristine();
                    });

                    this.calculateSums();

                    this.disableorEnableForms();
                    if (this.formData.orginal) {
                        this.mainPlanning = Utils.mainPlanning(this.formData.orginal);
                    } else {
                        this.addDefaults();
                    }
                })
        );

        const beginDate = pheMainForm.get('begindate');
        const endDate = pheMainForm.get('enddate');

        this.subscriptions.add(
            combineLatest([
                beginDate?.valueChanges.pipe(startWith(beginDate.value)),
                endDate?.valueChanges.pipe(startWith(endDate.value)),
            ]).subscribe(([begindate, enddate]) => {
                if (!begindate || !enddate) {
                    return;
                }

                this.subscriptions.add(
                    this.leaveService.getFilteredList(new Date(begindate), new Date(enddate)).subscribe(leaves => {
                        this.leaves = leaves;

                        this.setEmployeesLeave();
                    })
                );
            })
        );
    }

    getPerformerIds() {
        return ['performer', 'asphalt_performer']
            .map(field => this.formData.form.controls.planning?.get(field)?.value as number)
            .filter(id => !!id);
    }

    setEmployeesLeave() {
        this.formData.form.controls.employeePlanning.controls.forEach(fg => {
            this.setEmployeeLeaves(fg.value.user_id);
        });
    }

    setEmployeeLeaves(userId: number) {
        const pheMain = this.formData.form.controls.mainPlanningHas;
        const pheEmployee = this.formData.form.controls?.employeePlanning.controls.find(
            c => c.value.user_id === userId
        );

        const employeeBegin = pheEmployee
            ? pheEmployee.controls.begindate.value
            : pheMain.controls.phe.get('begindate')?.value;
        const employeeEnd = pheEmployee
            ? pheEmployee.controls.enddate.value
            : pheMain.controls.phe.get('begindate')?.value;

        return (this.employeeLeaves[userId] = this.leaveService.getLeavesForUserOnDate(
            this.leaves,
            userId,
            employeeBegin,
            employeeEnd,
            4
        ));
    }

    addDefaults() {
        const inEmployeePlanning = this.formData.form.controls.employeePlanning.value.map(c => c.user_id);
        const inPlanningHas = this.formData.form.controls.planningHas.controls.map(fg => fg.value.phe?.driver_user_id);

        this.employeeService
            .getDefaultEmployees(
                this.employees,
                this.mainPlanning?.entity?.default_employees ?? [],
                this.getPerformerIds()
            )
            .filter(
                e =>
                    !inEmployeePlanning.includes(e.id) &&
                    !inPlanningHas.includes(e.id) &&
                    this.employees.some(emp => emp.id === e.id)
            )
            .forEach(user => this.addEmployee(user.id));
    }

    ngOnChanges() {
        this.disableorEnableForms();
    }

    selectAddress(control: FormGroup<EmployeePlanningFg>) {
        if (this.formData.form.controls.employeePlanning.disabled) {
            return;
        }

        this.dialog.open(AddressDialogComponent, {
            width: '700px',
            height: '530px',
            maxHeight: '600px',
            data: {
                control,
                user: this.employees.find(e => e.id === control.value.user_id),
                location: this.formData.form.value.planning.location,
            },
        });
    }

    filterEmployees() {
        const currentEmployees = this.formData.form.controls.employeePlanning.value.map(e => e.user_id);

        const employeeItems: EmployeeSelectItem[] = this.employees
            .filter(e => currentEmployees.indexOf(e.id) === -1)
            .map(e => {
                return { ...e, leaves: this.employeeLeaves[e.id] ?? this.setEmployeeLeaves(e.id) };
            });

        const seenHiring = new Map<string, HiringSelectItem>();
        this.planningService
            .getCurrentPlannings()
            .flatMap(p => p.planning_has ?? [])
            .filter(phe => phe.entitytype_key === 'hiringStaff' && phe.name)
            .forEach(phe => {
                const key = `${phe.name}|${phe.hiring_id ?? ''}`;
                if (!seenHiring.has(key)) {
                    seenHiring.set(key, {
                        id: null,
                        name: phe.name,
                        companyName: phe.hiring?.name,
                        hiringId: phe.hiring_id,
                        isHiring: true,
                        function: phe.hiring?.name,
                    });
                }
            });

        this.filteredEmployees = [...employeeItems, ...Array.from(seenHiring.values())];
    }

    calculateSums() {
        setTimeout(() => {
            this.filterEmployees();
            this.sums = [];
            if (this.formData.orginal?.traffic_controllers > 0) {
                this.sums.push({
                    function: 'Verkeersregelaar',
                    amount: 0,
                });
            }
            this.formData.form.controls.employeePlanning.value.forEach(employee => {
                const sum = this.sums.find(p => p.function == employee.function);
                if (sum) {
                    sum.amount++;
                } else {
                    this.sums.push({
                        function: employee.function,
                        amount: 1,
                    });
                }
            });
            if (this.hiringStaffPheFields?.length) {
                const hiringCompanyMap = new Map<number, string>();
                this.planningService
                    .getCurrentPlannings()
                    .flatMap(p => p.planning_has ?? [])
                    .filter(phe => phe.hiring_id && phe.hiring?.name)
                    .forEach(phe => hiringCompanyMap.set(phe.hiring_id, phe.hiring.name));

                this.hiringStaffPheFields.forEach(pheField => {
                    const hiringId = pheField.form.controls.phe.get('hiring_id')?.value as number;
                    const companyName = hiringId
                        ? (pheField.orginal?.hiring?.name ?? hiringCompanyMap.get(hiringId))
                        : null;
                    const label = companyName ?? 'Inhuur';
                    const sum = this.sums.find(p => p.function === label);
                    if (sum) {
                        sum.amount++;
                    } else {
                        this.sums.push({ function: label, amount: 1 });
                    }
                });
            }
        });
    }

    private createEmployeeFg(
        userId: number,
        defaults: Partial<{
            id: number;
            _moved_from_id: number;
            planning_id: number;
            begindate: Date;
            enddate: Date;
            comment: string;
            updated_at: Date;
            origin: string;
            destination: string;
            work_begin: string;
            work_end: string;
        }> = {},
        movedFrom?: UserPlanning
    ): FormGroup<EmployeePlanningFg> {
        const phe = this.formData.form.controls.mainPlanningHas.controls.phe;
        const employee = this.employees.find(e => e.id === userId);
        const address = this.locationService.formatAddress(environment.companyAddress);
        const location = this.formData.form.controls.planning?.get('location')?.value;

        const fg = new FormGroup<EmployeePlanningFg>(
            {
                id: new FormControl(defaults.id ?? null),
                _moved_from_id: new FormControl(defaults._moved_from_id ?? null),
                user_id: new FormControl(userId),
                planning_id: new FormControl(defaults.planning_id ?? this.formData.orginal?.id),
                begindate: new FormControl(defaults.begindate ?? phe.get('begindate').value, Validators.required),
                enddate: new FormControl(defaults.enddate ?? phe.get('enddate').value, Validators.required),
                conflictText: new FormControl(''),
                comment: new FormControl(defaults.comment ?? null),
                function: new FormControl(employee?.function),
                employee: new FormControl(employee?.name),
                updated_at: new FormControl(defaults.updated_at ?? null),
                origin: new FormControl(defaults.origin ?? address),
                destination: new FormControl(defaults.destination ?? address),
                work_begin: new FormControl(defaults.work_begin ?? location),
                work_end: new FormControl(defaults.work_end ?? location),
            },
            [],
            localEmployeeAvailableValidator(
                this.userPlanningService,
                this.planningHasService,
                movedFrom ?? null,
                this.formData?.orginal?.id,
                () =>
                    Array.from(this.formData.form.controls.planningHas?.getRawValue()?.map(ph => ph.phe) ?? []) as Pick<
                        PlanningHasEntity,
                        'id' | 'driver_user_id' | 'begindate' | 'enddate'
                    >[],
                () =>
                    Array.from(this.formData.form?.controls?.employeePlanning?.value) as Pick<
                        UserPlanning,
                        'id' | 'user_id' | 'begindate' | 'enddate'
                    >[]
            )
        );

        this.subscriptions.add(
            fg.controls.begindate.valueChanges.subscribe(() => {
                fg.controls.enddate.updateValueAndValidity();
            })
        );

        this.subscriptions.add(
            fg.controls.enddate.valueChanges.subscribe(enddate => {
                const begindate = new Date(fg.controls.begindate.value);

                if (!enddate) {
                    return;
                }

                enddate = new Date(enddate);

                begindate.setSeconds(0);

                const endTime = new Date(begindate);

                endTime.setHours(enddate.getHours());
                endTime.setMinutes(enddate.getMinutes());

                if (endTime < begindate && enddate <= begindate) {
                    enddate.setDate(enddate.getDate() + 1);

                    fg.controls.enddate.setValue(enddate, { emitEvent: false, onlySelf: true });
                }
                if (endTime > begindate && enddate.getDate() !== begindate.getDate()) {
                    enddate.setDate(begindate.getDate());

                    fg.controls.enddate.setValue(enddate, { emitEvent: false, onlySelf: true });
                }
            })
        );

        return fg;
    }

    addEmployee(userId: number) {
        const fg = this.createEmployeeFg(userId);

        this.setEmployeeLeaves(userId);

        this.fcEmployee.reset();

        this.formData.form.controls.employeePlanning.push(fg);

        this.filterFunction = null;

        this.calculateSums();

        this.disableorEnableForms();

        this.formData.form.markAsDirty();
        this.formData.form.updateValueAndValidity();
    }

    deleteAll() {
        this.confirmDialog
            .confirm({
                title: 'Verwijderen',
                text: `Weet u zeker dat u al het personeel wilt verwijderen van deze planning?`,
                confirm: 'Verwijderen',
                cancel: 'Behouden',
            })
            .then(
                () => {
                    this.formData.form.controls.employeePlanning.clear();
                    [...this.hiringStaffPheFields].forEach(pheField => {
                        this.entityTypeService.removePheFromForm(this.formData, pheField);
                    });
                    this.refreshHiringStaffPheFields();
                    this.formData.form.markAsDirty();
                    this.formData.form.updateValueAndValidity();
                    this.calculateSums();
                },
                () => {}
            );
    }

    delete(formgroup: FormGroup<EmployeePlanningFg>) {
        this.deleteEmployeeFg(formgroup);
    }

    private deleteEmployeeFg(formgroup: FormGroup<EmployeePlanningFg>) {
        if (formgroup) {
            this.formData.form.controls.employeePlanning.controls.splice(
                this.formData.form.controls.employeePlanning.controls.indexOf(formgroup),
                1
            );
            this.formData.form.controls.employeePlanning.value.splice(
                this.formData.form.controls.employeePlanning.value.indexOf(formgroup.value),
                1
            );
            this.formData.form.controls.employeePlanning.updateValueAndValidity();
            this.formData.form.controls.planningHas.controls.forEach(fg => {
                if (fg.controls.phe.get('driver_user_id')) {
                    fg.controls.phe.get('driver_user_id').updateValueAndValidity();
                }
            });
            this.formData.form.markAsDirty();
            this.formData.form.updateValueAndValidity();
            this.calculateSums();
        }
    }

    disableorEnableForms() {
        if (this.formData.mainPhe.orginal) {
            const entityType = environment.entitytypes[this.formData.mainPhe.orginal.entitytype_key];
            if (!this.entityTypeService.userCanEditPlanning(entityType, this.formData.orginal)) {
                this.formData.form.controls.employeePlanning?.controls.forEach(form => form.disable());
                this.formData.form.controls.employeePlanning.disable();
            }
        }
    }

    private refreshHiringStaffPheFields() {
        this.hiringStaffPheFields = this.formData.pheFields.filter(
            pheField => pheField.form.controls.phe.get('entitytype_key')?.value === 'hiringStaff'
        );
    }

    onEmployeeChange(item: EmployeeSelectItem) {
        if (!item) {
            return;
        }
        if (item.isHiring) {
            this.addHiringPhe(item.name, item.hiringId);
        } else {
            this.addEmployee(item.id);
        }
    }

    addHiringPhe(name: string, hiringId?: number) {
        const phe = this.entityTypeService.addPheToForm(this.formData, this.hiringStaffEntityType);
        (phe.form.controls.phe.get('entity_id') as unknown as FormControl).setValue(EntityId.HiringEmployee);
        setTimeout(() => {
            (phe.form.controls.phe.get('name') as unknown as FormControl).setValue(name);
            if (hiringId) {
                (phe.form.controls.phe.get('hiring_id') as unknown as FormControl).setValue(hiringId);
            }
        });
        const fcPerformer = this.formData.form.controls.planning.get('performer');
        if (!fcPerformer.value) {
            this.formData.form.controls.planning.get('performer').setValue(UserId.HiringPerformer);
        }

        this.fcEmployee.reset();
        this.formData.form.markAsDirty();
        this.calculateSums();
    }

    removeHiringPhe(pheField: PheFormData) {
        this.entityTypeService.removePheFromForm(this.formData, pheField);
        this.refreshHiringStaffPheFields();
        this.calculateSums();
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }
}
